import { callAPIUncredentialed, getPublicApiUrl, HTTPMethod } from './API'
import {
    ScansListParams,
    ScansListResponse,
    ScansParams,
    ScansResponse,
} from '../model'
import { translate } from '../i18n'

const getApi = (projectId: string) => `/projects/${projectId}/analytics/scans`

const getListApi = (projectId: string) =>
    `/projects/${projectId}/analytics/scans/list`

const getExportApi = (projectId: string) =>
    `/projects/${projectId}/analytics/scans/export`

const buildQuery = (params: ScansParams): Record<string, string> => {
    const query: Record<string, string> = {}
    if (params.from) {
        query.from = params.from
    }
    if (params.to) {
        query.to = params.to
    }
    if (params.platform) {
        query.platform = params.platform
    }
    return query
}

export async function getProjectScans(
    projectId: string,
    params: ScansParams
): Promise<ScansResponse> {
    const options = {
        method: HTTPMethod.GET,
        query: buildQuery(params),
    }
    // Embedded (third-party) contexts have no session; sending no credentials
    // keeps the request CORS-simple so the public endpoint can use a wildcard
    // Access-Control-Allow-Origin.
    return await callAPIUncredentialed(getApi(projectId), options)
}

/**
 * Fetches a page of individual scan events (newest first) for the scans list.
 * Public endpoint, same CORS-simple contract as {@link getProjectScans}.
 */
export async function getProjectScansList(
    projectId: string,
    params: ScansListParams
): Promise<ScansListResponse> {
    const query = buildQuery(params)
    if (params.page) {
        query.page = `${params.page}`
    }
    if (params.limit) {
        query.limit = `${params.limit}`
    }
    const options = {
        method: HTTPMethod.GET,
        query,
    }
    return await callAPIUncredentialed(getListApi(projectId), options)
}

/** Thrown on HTTP 429 so callers can apply the Retry-After cooldown. */
export class RateLimitedError extends Error {
    readonly retryAfterSeconds: number

    constructor(message: string, retryAfterSeconds: number) {
        super(message)
        this.name = 'RateLimitedError'
        this.retryAfterSeconds = retryAfterSeconds
    }
}

const DEFAULT_RETRY_AFTER_SECONDS = 30

const deriveExportFilename = (projectId: string): string => {
    const now = new Date()
    const pad = (n: number) => `${n}`.padStart(2, '0')
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
        now.getDate()
    )}`
    return `${projectId}-scans-${stamp}.csv`
}

/**
 * Downloads the scans CSV export, honoring the current filters. Public
 * endpoint: the request is sent without credentials or custom headers (keeps
 * it CORS-simple), so it also works from third-party iframe embeds.
 */
export async function downloadProjectScansCsv(
    projectId: string,
    params: ScansParams
): Promise<void> {
    const url = getPublicApiUrl(getExportApi(projectId), buildQuery(params))

    const response = await fetch(url, {
        method: HTTPMethod.GET,
        mode: 'cors',
        credentials: 'omit',
    })

    if (response.status === 429) {
        const retryAfter = Number(response.headers.get('Retry-After'))
        throw new RateLimitedError(
            translate('projects.analytics.export.rate_limited') as string,
            Number.isFinite(retryAfter) && retryAfter > 0
                ? retryAfter
                : DEFAULT_RETRY_AFTER_SECONDS
        )
    }

    if (!response.ok) {
        let message = translate('projects.analytics.export.failed') as string
        try {
            const apiError = await response.json()
            if (apiError?.code) {
                message = translate(`errors.codes.${apiError.code}`) as string
            }
        } catch {
            // Non-JSON error body; keep the generic message.
        }
        throw new Error(message)
    }

    // Content-Disposition is only readable cross-origin when exposed via
    // Access-Control-Expose-Headers; fall back to a derived filename.
    const disposition = response.headers.get('Content-Disposition')
    const filename =
        disposition?.match(/filename="(.+)"/)?.[1] ??
        deriveExportFilename(projectId)

    const blob = await response.blob()
    const anchor = document.createElement('a')
    anchor.href = URL.createObjectURL(blob)
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(anchor.href)
}
